import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import AppLayout from "@/components/layout/AppLayout";
import { PageFallback } from "@/components/store/Skeletons";
import { RequireAuth, RequireAdmin } from "@/components/RequireAuth";
import AuthCallback from "@/pages/AuthCallback";

const Home = lazy(() => import("@/pages/Home"));
const Topups = lazy(() => import("@/pages/Topups"));
const Topup = lazy(() => import("@/pages/Topup"));
const Smm = lazy(() => import("@/pages/Smm"));
const Orders = lazy(() => import("@/pages/Orders"));
const Profile = lazy(() => import("@/pages/Profile"));
const WalletTopup = lazy(() => import("@/pages/WalletTopup"));
const StaticPage = lazy(() => import("@/pages/StaticPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Login = lazy(() => import("@/pages/auth/Login"));
const Register = lazy(() => import("@/pages/auth/Register"));
const ForgotPassword = lazy(() => import("@/pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/auth/ResetPassword"));
const AdminLogin = lazy(() => import("@/pages/auth/AdminLogin"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminContent = lazy(() => import("@/pages/admin/AdminContent"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminOrders = lazy(() => import("@/pages/admin/AdminOrders"));
const AdminTopups = lazy(() => import("@/pages/admin/AdminTopups"));
const AdminPayments = lazy(() => import("@/pages/admin/AdminPayments"));
const AdminIntegrations = lazy(() => import("@/pages/admin/AdminIntegrations"));
const AdminReports = lazy(() => import("@/pages/admin/AdminReports"));
const AdminSupport = lazy(() => import("@/pages/admin/AdminSupport"));
const AdminNotifications = lazy(() => import("@/pages/admin/AdminMarketing").then((m) => ({ default: m.AdminNotifications })));
const AdminCoupons = lazy(() => import("@/pages/admin/AdminMarketing").then((m) => ({ default: m.AdminCoupons })));
const AdminStaff = lazy(() => import("@/pages/admin/AdminSystem").then((m) => ({ default: m.AdminStaff })));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSystem").then((m) => ({ default: m.AdminSettings })));
const AdminStoreCategory = lazy(() => import("@/pages/admin/store/AdminStoreCategory"));
const AdminStoreProducts = lazy(() => import("@/pages/admin/store/AdminStoreProducts"));
const AdminStoreProductForm = lazy(() => import("@/pages/admin/store/AdminStoreProductForm"));
const AdminStoreOrders = lazy(() => import("@/pages/admin/store/AdminStoreOrders"));
const AdminComingSoon = lazy(() => import("@/pages/admin/AdminComingSoon"));

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Suspense fallback={<PageFallback full />}>
      <Routes>
        <Route element={<AppLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/topups" element={<Topups />} />
          <Route path="/topup/:slug" element={<Topup />} />
          <Route path="/smm" element={<Smm />} />
          <Route path="/orders" element={<RequireAuth><Orders /></RequireAuth>} />
          <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
          <Route path="/about" element={<StaticPage slug="about" />} />
          <Route path="/privacy" element={<StaticPage slug="privacy" />} />
          <Route path="/terms" element={<StaticPage slug="terms" />} />
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route path="/wallet/top-up" element={<RequireAuth><WalletTopup /></RequireAuth>} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/admin-login" element={<AdminLogin />} />
        <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="catalog" element={<AdminContent />} />
          <Route path="topups" element={<AdminTopups />} />
          <Route path="store/category" element={<AdminStoreCategory />} />
          <Route path="store/products" element={<AdminStoreProducts />} />
          <Route path="store/product/create-product" element={<AdminStoreProductForm />} />
          <Route path="store/product/:id/edit" element={<AdminStoreProductForm />} />
          <Route path="store/orders" element={<AdminStoreOrders />} />
          <Route path="smm-services" element={<AdminComingSoon title="SMM Services" />} />
          <Route path="smm-history" element={<AdminComingSoon title="SMM Order History" />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="integrations" element={<AdminIntegrations />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="content" element={<AdminContent />} />
          <Route path="support" element={<AdminSupport />} />
          <Route path="notifications" element={<AdminNotifications />} />
          <Route path="coupons" element={<AdminCoupons />} />
          <Route path="staff" element={<AdminStaff />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster theme="dark" position="top-center" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}
